import React, {useId, useMemo} from 'react'
import PropTypes from 'prop-types'
import {Box, useSlotRecipe} from '@chakra-ui/react3'
import {useIntl} from 'react-intl'
import NavigationParent from '../../navigation-parent'
import NavigationLink from '../../navigation-link'
import NavigationSeparator from '../../navigation-separator'
import NavigationHide from '../../hide'
import {ChevronLeftIcon} from '../../../icons'
import NavigationTeaser, {hasNavigationTeaserContent} from '../../navigation-teaser'
import {flyoutNavigationLevel2Recipe} from './flyout-navigation-level2.recipe'
import RichText from '../../../cms/rich-text'
import {itemIndex, withStaggerIndices} from './flyout-stagger'
import {trackNavigationClick} from '../../../../../tracking/analytics'

/**
 * The level-2 entry types, discriminated on `item.type`.
 *
 * A parent opens level 3 and never navigates; a link navigates. Both carry the
 * same `variant` object, so the two render identically apart from that.
 */
const NAV_LVL_2_MAP = {
    navigationParentLevel2: NavigationParent,
    navigationLink: NavigationLink,
    navigationSeparator: NavigationSeparator
}

/** Entries whose type has no component render nothing at all. */
const isRenderableLevel2 = (item) => Boolean(NAV_LVL_2_MAP[item.type])

/**
 * Resolves an entry's render props from its `item.type`, mirroring the component
 * lookup in `NAV_LVL_2_MAP`.
 *
 * A separator passes its CMS shape straight through; `NavigationSeparator`
 * resolves the variant and text itself. A link or parent passes its own fields,
 * and both pass the CMS `variant` object through as authored, which is what
 * `NavigationLink` and `NavigationParent` render their card image/copy/price and
 * accent colour from. A level-2 parent reports its level-3 drill-in directly;
 * the CMS contract supplies one of the supported level-3 subtree shapes.
 *
 * @param {Object} item - The level-2 entry.
 * @param {Object} handlers
 * @param {Function} handlers.onDrillIn - Called with the entry when it opens level 3.
 * @returns {Object} Props for the resolved component.
 */
const resolveLevel2ItemProps = (item, {onDrillIn}) => {
    if (item.type === 'navigationSeparator') {
        return item
    }

    const props = {title: item.title, link: item.link}
    // A link navigates straight to its target, so it renders NavigationLink;
    // only a drill-in parent carries a chevron affordance.
    if (item.type !== 'navigationParentLevel2')
        return {
            ...props,
            variant: item.variant,
            onClick: () => trackNavigationClick(2, item.title)
        }

    return {
        ...props,
        variant: item.variant,
        onClick: () => {
            trackNavigationClick(2, item.title)
            onDrillIn(item)
        }
    }
}
/**
 * Level-2 pane of the flyout navigation.
 *
 * Three columns - the main teaser, the navigation-item list, and the optional
 * teaser column - under a drill-out control that only the mobile layout shows.
 * Mobile stacks them; desktop sits them beside the level-1 list, which stays on
 * screen. Both viewports render this identically: see `DRILLED_IN` in
 * `flyout-navigation.recipe.js` for how the layouts diverge, and
 * `flyout-navigation-level2.recipe.js` for the columns themselves.
 *
 * Selecting a level-2 parent calls `onDrillIn` instead of rendering level 3
 * here: level 3 replaces the level-1 list as well as this pane, so the pane it
 * swaps into has to be a sibling of both (see `flyout-navigation.jsx` and
 * `DRILLED_IN_LEVEL3`).
 *
 * @component
 * @param {Object} props
 * @param {Object} [props.module] - The `level2Module` resolved onto the active
 *   level-1 entry by `useNavigationContent`.
 * @param {string} [props.title] - Title of the active level-1 entry, shown
 *   beside the drill-out control so the mobile pane names where it came from.
 * @param {Function} props.onBack - Clears the selection, returning mobile to
 *   the level-1 pane.
 * @param {Function} props.onDrillIn - Called with the selected level-2 parent.
 * @returns {JSX.Element|null}
 */
const FlyoutNavigationLevel2 = ({module, title, onBack, onDrillIn}) => {
    const recipe = useSlotRecipe({recipe: flyoutNavigationLevel2Recipe})
    const styles = useMemo(() => recipe(), [recipe])
    const {formatMessage} = useIntl()
    const titleId = useId()

    const elements = useMemo(
        () => (module?.elements || []).map(({item}) => item).filter(Boolean),
        [module]
    )

    // The CMS template emits `mainTeaser` whether or not one was authored, so
    // presence says nothing about whether there is anything to show; the teaser
    // column is a list and only carries what an author added, but an entry there
    // can still be left blank (STOK-17335). Derived above the early return
    // because these are hooks, and read only below it.
    const hasMainTeaser = useMemo(() => hasNavigationTeaserContent(module?.mainTeaser), [module])
    const teasers = useMemo(
        () => (module?.teasers || []).filter(hasNavigationTeaserContent),
        [module]
    )

    // Renderable rows paired with their place in the staggered reveal.
    const staggeredElements = useMemo(
        () => withStaggerIndices(elements, isRenderableLevel2),
        [elements]
    )

    if (!module) return null

    return (
        <Box css={styles.root}>
            <Box
                as="button"
                type="button"
                css={styles.breadcrumb}
                onClick={onBack}
                aria-label={formatMessage({
                    id: 'leap.navigation.level2.back',
                    defaultMessage: 'Back to main navigation'
                })}
            >
                <Box css={styles.breadcrumbIcon} aria-hidden="true">
                    <ChevronLeftIcon />
                </Box>
                <RichText as="span" id={titleId} css={styles.breadcrumbTitle} aria-hidden="true">
                    {title}
                </RichText>
            </Box>
            <Box css={styles.columns}>
                <Box css={styles.mainTeaser}>
                    {hasMainTeaser && (
                        <NavigationHide hide={module.mainTeaser.hide}>
                            <NavigationTeaser {...module.mainTeaser} variant="large" />
                        </NavigationHide>
                    )}
                </Box>
                {/* Named by the breadcrumb title, which on mobile — level-1 list
                    off screen, drill-out control announcing only itself — is the
                    one thing left naming this pane. By reference to the rendered
                    node rather than the CMS `title`, so the authored markup stays
                    out of the name; `aria-hidden` does not stop an explicit
                    reference reading its text. */}
                <Box as="ul" css={styles.items} aria-labelledby={titleId}>
                    {staggeredElements.map(({item, mobile, desktop}, idx) => {
                        const Component = NAV_LVL_2_MAP[item.type]

                        const props = resolveLevel2ItemProps(item, {onDrillIn})

                        return (
                            <NavigationHide asChild hide={item.hide} key={idx}>
                                <Box as="li" css={styles.item} style={itemIndex({mobile, desktop})}>
                                    <Component {...props} />
                                </Box>
                            </NavigationHide>
                        )
                    })}
                </Box>
                <Box css={styles.teasers}>
                    {teasers.map((teaser, idx) => (
                        <NavigationHide hide={teaser.hide} key={idx}>
                            <NavigationTeaser {...teaser} variant="small" />
                        </NavigationHide>
                    ))}
                </Box>
            </Box>
        </Box>
    )
}

FlyoutNavigationLevel2.propTypes = {
    module: PropTypes.object,
    title: PropTypes.string,
    onBack: PropTypes.func.isRequired,
    onDrillIn: PropTypes.func.isRequired
}

if (process.env.NODE_ENV !== 'production') {
    FlyoutNavigationLevel2.displayName = 'FlyoutNavigationLevel2'
}

export default FlyoutNavigationLevel2
